import {
  CCAApplicantResult,
  CCACandidatesListResponse,
  CCAConfig,
  CCAPresignImageResponse,
  CCAQuestion,
  CCAQuestionsListResponse,
  CCAQuickBuildResponse,
  CCAResultsListResponse,
  CreateCCAQuestionRequest,
  CreateOrUpdateCCAConfigRequest,
  ListCCACandidatesParams,
  ListCCAQuestionsParams,
  SendCCAInviteRequest,
  SendCCAInviteResponse,
  StorageUrlResponse,
  UpdateCCAConfigQuestionsRequest,
  UpdateCCAQuestionRequest,
} from '../types/cca_types';
import { rtkApi } from './rtkApi';

export const ccaApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Questions ───────────────────────────────────────────────────────────

    listCCAQuestions: builder.query<
      CCAQuestionsListResponse,
      ListCCAQuestionsParams
    >({
      query: ({
        subdomain,
        level,
        question_type,
        page = 1,
        page_size = 20,
      }) => {
        const params = new URLSearchParams();
        if (subdomain) params.set('subdomain', subdomain);
        if (level !== undefined) params.set('level', String(level));
        if (question_type) params.set('question_type', question_type);
        params.set('page', String(page));
        params.set('page_size', String(page_size));
        return `/cca/questions?${params.toString()}`;
      },
      transformResponse: (raw: {
        questions?: CCAQuestion[];
        items?: CCAQuestion[];
        total: number;
        page: number;
        pageSize?: number;
        page_size?: number;
      }): CCAQuestionsListResponse => ({
        items: raw.questions ?? raw.items ?? [],
        total: raw.total,
        page: raw.page,
        page_size: raw.pageSize ?? raw.page_size ?? 25,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.items.map(({ id }) => ({
              type: 'CCAQuestion' as const,
              id,
            })),
            { type: 'CCAQuestion' as const, id: 'LIST' },
          ]
          : [{ type: 'CCAQuestion' as const, id: 'LIST' }],
    }),

    createCCAQuestion: builder.mutation<CCAQuestion, CreateCCAQuestionRequest>({
      query: (body) => ({
        url: '/cca/questions',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CCAQuestion', id: 'LIST' }],
    }),

    updateCCAQuestion: builder.mutation<
      CCAQuestion,
      { questionId: string; body: UpdateCCAQuestionRequest }
    >({
      query: ({ questionId, body }) => ({
        url: `/cca/questions/${questionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { questionId }) => [
        { type: 'CCAQuestion', id: questionId },
        { type: 'CCAQuestion', id: 'LIST' },
      ],
    }),

    deleteCCAQuestion: builder.mutation<void, string>({
      query: (questionId) => ({
        url: `/cca/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, questionId) => [
        { type: 'CCAQuestion', id: questionId },
        { type: 'CCAQuestion', id: 'LIST' },
      ],
    }),

    // ─── Images ──────────────────────────────────────────────────────────────

    presignCCAImageUpload: builder.mutation<CCAPresignImageResponse, string>({
      query: (fileName) => ({
        url: `/cca/images/presign?file_name=${encodeURIComponent(fileName)}`,
        method: 'POST',
      }),
    }),
    getStorageUrl: builder.query<StorageUrlResponse, string>({
      query: (filePath) => `/storage?file_path=${encodeURIComponent(filePath)}`,
    }),
    // ─── Config ──────────────────────────────────────────────────────────────

    getCCAConfig: builder.query<CCAConfig, string>({
      query: (jobOpeningId) => `/cca/config/${jobOpeningId}`,
      transformResponse: (raw: {
        config?: CCAConfig;
        questions?: CCAConfig['questions'];
      }): CCAConfig => ({
        ...(raw.config ?? (raw as CCAConfig)),
        questions: raw.questions,
      }),
      providesTags: (result, error, jobOpeningId) => [
        { type: 'CCAConfig', id: jobOpeningId },
      ],
    }),

    getPublicCCAConfig: builder.query<
      CCAConfig,
      { jobOpeningId: string; token: string }
    >({
      query: ({ jobOpeningId, token }) => ({
        url: `/cca/config/${jobOpeningId}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      transformResponse: (raw: {
        config?: CCAConfig;
        questions?: CCAConfig['questions'];
      }): CCAConfig => ({
        ...(raw.config ?? (raw as CCAConfig)),
        questions: raw.questions,
      }),
      providesTags: (result, error, { jobOpeningId }) => [
        { type: 'CCAConfig', id: jobOpeningId },
      ],
    }),

    createOrUpdateCCAConfig: builder.mutation<
      CCAConfig,
      { jobOpeningId: string; body: CreateOrUpdateCCAConfigRequest }
    >({
      query: ({ jobOpeningId, body }) => ({
        url: `/cca/config/${jobOpeningId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [
        { type: 'CCAConfig', id: jobOpeningId },
      ],
    }),

    quickBuildCCAConfig: builder.mutation<CCAQuickBuildResponse, string>({
      query: (jobOpeningId) => ({
        url: `/cca/config/${jobOpeningId}/quick-build`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, jobOpeningId) => [
        { type: 'CCAConfig', id: jobOpeningId },
      ],
    }),

    updateCCAConfigQuestions: builder.mutation<
      string,
      { jobOpeningId: string; body: UpdateCCAConfigQuestionsRequest }
    >({
      query: ({ jobOpeningId, body }) => ({
        url: `/cca/config/${jobOpeningId}/questions`,
        method: 'PUT',
        body,
        responseHandler: 'text',
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [
        { type: 'CCAConfig', id: jobOpeningId },
      ],
    }),

    // ─── Candidates ──────────────────────────────────────────────────────────

    listCCACandidates: builder.query<
      CCACandidatesListResponse,
      { jobOpeningId: string } & ListCCACandidatesParams
    >({
      query: ({ jobOpeningId, cca_status, page = 1, page_size = 20 }) => {
        const params = new URLSearchParams();
        if (cca_status) params.set('cca_status', cca_status);
        params.set('page', String(page));
        params.set('page_size', String(page_size));
        return `/cca/candidates/${jobOpeningId}?${params.toString()}`;
      },
      providesTags: (result, error, { jobOpeningId }) => [
        { type: 'CCACandidate', id: jobOpeningId },
      ],
    }),

    sendCCAInvite: builder.mutation<
      SendCCAInviteResponse,
      SendCCAInviteRequest
    >({
      query: ({ applicantIds }) => ({
        url: '/cca/send-invite',
        method: 'POST',
        body: { applicantIds },
      }),
      invalidatesTags: (result, error, { jobOpeningId }) =>
        jobOpeningId
          ? [{ type: 'CCACandidate', id: jobOpeningId }]
          : [{ type: 'CCACandidate', id: 'LIST' }],
    }),

    // ─── Results ─────────────────────────────────────────────────────────────

    getCCAResults: builder.query<CCAResultsListResponse, string>({
      query: (jobOpeningId) => `/cca/results/${jobOpeningId}`,
      providesTags: (result, error, jobOpeningId) => [
        { type: 'CCAResult', id: jobOpeningId },
      ],
    }),

    getCCAApplicantResult: builder.query<
      CCAApplicantResult,
      { jobOpeningId: string; applicantId: string }
    >({
      query: ({ jobOpeningId, applicantId }) =>
        `/cca/results/${jobOpeningId}/${applicantId}`,
      providesTags: (result, error, { applicantId }) => [
        { type: 'CCAResult', id: applicantId },
      ],
    }),
  }),
});

export const {
  // Questions
  useListCCAQuestionsQuery,
  useLazyListCCAQuestionsQuery,
  useCreateCCAQuestionMutation,
  useUpdateCCAQuestionMutation,
  useDeleteCCAQuestionMutation,
  // Images
  usePresignCCAImageUploadMutation,
  useGetStorageUrlQuery,
  // Config
  useGetCCAConfigQuery,
  useGetPublicCCAConfigQuery,
  useLazyGetCCAConfigQuery,
  useCreateOrUpdateCCAConfigMutation,
  useQuickBuildCCAConfigMutation,
  useUpdateCCAConfigQuestionsMutation,
  // Candidates
  useListCCACandidatesQuery,
  useLazyListCCACandidatesQuery,
  useSendCCAInviteMutation,
  // Results
  useGetCCAResultsQuery,
  useLazyGetCCAResultsQuery,
  useGetCCAApplicantResultQuery,
  useLazyGetCCAApplicantResultQuery,
} = ccaApi;
